import { AxiosError } from "axios";

import { ADMIN_API_ENDPOINTS } from "@/config/apiEndpoints/adminUrls";
import { handleApiError } from "@/lib/utils";
import adminAxios from "@/lib/axios";

export interface IProgramCardsMetadata {
  id?: string;
  meta_thumbnail_image?: string;
  meta_short_course_name?: string;
  meta_short_course_name_text_color?: string;
  meta_full_course_name?: string;
  meta_course_name_text_color?: string;
  meta_subtext?: string;
  meta_subtext_color?: string;
  meta_enroll_icon?: string;
  meta_enroll_text?: string;
  meta_enroll_text_color?: string;
  meta_money_icon?: string;
  meta_money_icon_color?: string;
  meta_brochure_icon?: string;
  meta_brochure_text?: string;
  meta_brochure_text_color?: string;
  meta_brochure_button_background_color?: string;
  meta_cta_button_text?: string;
  meta_cta_button_text_color?: string;
  meta_cta_button_background_color?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

export interface IProgramCardsCard {
  id?: number;
  category_id: number;
  image?: string;
  image_alt?: string;
  title?: string;
  title_color?: string;
  subtitle?: string;
  subtitle_color?: string;
  duration_heading?: string;
  duration_text?: string;
  duration_icon?: string;
  duration_icon_alt?: string;
  duration_heading_color?: string;
  duration_text_color?: string;
  format_heading?: string;
  format_text?: string;
  format_icon?: string;
  format_icon_alt?: string;
  format_heading_color?: string;
  format_text_color?: string;
  what_you_gain_heading?: string;
  what_you_gain_text?: string;
  what_you_gain_icon?: string;
  what_you_gain_icon_alt?: string;
  what_you_gain_heading_color?: string;
  what_you_gain_text_color?: string;
  modal_title?: string;
  modal_title_color?: string;
  modal_title_background_start_color?: string;
  modal_title_background_end_color?: string;
  modal_title_background_angle?: string;
  modal_description?: string;
  modal_cta_background_start_color?: string;
  modal_cta_background_end_color?: string;
  modal_cta_background_angle?: string;
  modal_cta_text_color?: string;
  modal_cta_text?: string;
  button_text?: string;
  button_text_color?: string;
  button_background_color?: string;
  button_icon?: string;
  button_icon_alt?: string;
  brochure_button_text?: string;
  brochure_button_text_color?: string;
  brochure_button_background_color?: string;
  brochure_button_icon?: string;
  brochure_button_icon_alt?: string;
  brochure_link?: string;
  priority?: number;
  status?: boolean;
}

export interface IProgramCardsCategory {
  id?: number;
  program_cards_id?: number;
  name: string;
  order_index: number;
  is_default?: boolean;
  status?: boolean;
  text_color?: string;
  background_color?: string;
  active_text_color?: string;
  active_background_color?: string;
  border_color?: string;
  cards?: IProgramCardsCard[];
}

export interface IProgramCards {
  id?: number | string;
  page_id?: number;
  component_id?: number;
  title?: string;
  title_text_color?: string;
  title_1?: string;
  title_1_color?: string;
  title_2?: string;
  title_2_gradient_start_color?: string;
  title_2_gradient_end_color?: string;
  title_2_color?: string;
  explore_course_text?: string;
  explore_course_text_color?: string;
  explore_course_border_color?: string;
  explore_course_icon?: string;
  explore_course_icon_alt?: string;
  explore_all_courses_text?: string;
  modal_cta_text?: string;
  desktop_background_color?: string;
  mobile_background_color?: string;
  backvariant?: string;
  font_family_variant?: string;
  gradient_color_1?: string;
  gradient_color_2?: string;
  gradient_color_3?: string;
  gradient_color_4?: string;
  pre_title?: string;
  pre_title_color?: string;
  title_start_color?: string;
  title_end_color?: string;
  title_gradient_angle?: string;
  cta_text?: string;
  cta_text_color?: string;
  cta_background_start_color?: string;
  cta_background_end_color?: string;
  cta_background_angle?: string;
  cta_icon?: string;
  cta_icon_alt?: string;
  cta_button_variant?: string;
  card_background_color?: string;
  load_more_text?: string;
  load_more_icon?: string;
  load_more_desktop_text_color?: string;
  load_more_mobile_text_color?: string;
  load_more_mobile_background_color?: string;
  show_features?: boolean;
  // Variant 4 ("Careers after course" carousel)
  description?: string;
  note?: string;
  background_image?: string;
  background_image_alt?: string;
  title_highlight_color?: string;
  salary_text_color?: string;
  key_skills_text_color?: string;
  key_skills_background_color?: string;
  key_skills_border_color?: string;
  enable_animation?: boolean | number;
  metadata?: IProgramCardsMetadata[];
  variant?: string;
  status?: boolean;
  categories?: IProgramCardsCategory[];
}

export async function createProgramCards(payload: Partial<IProgramCards>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_CARDS.CREATE,
      payload
    );

    if (response?.status === 201 || response?.status === 200) {
      const body = response.data as {
        success?: boolean;
        data?: { id?: number };
      } | null;
      if (body?.success && body.data?.id != null) {
        return body as typeof response.data;
      }
    }
    return null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateProgramCards(id: number, payload: Partial<IProgramCards>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_CARDS.UPDATE(id),
      payload
    );

    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getProgramCardsByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_CARDS.GET_BY_COMPONENT_ID(componentId)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function cloneProgramCardsWithCategories(
  sourceProgramCardsId: number,
  targetPageComponentId: number,
  targetProgramCardsId?: number | null
) {
  try {
    const response = await adminAxios.post(ADMIN_API_ENDPOINTS.PROGRAM_CARDS.CLONE, {
      source_program_cards_id: sourceProgramCardsId,
      target_page_component_id: targetPageComponentId,
      target_program_cards_id: targetProgramCardsId ?? null,
    });
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// Category (variant 2)
export async function createProgramCardsCategory(payload: Partial<IProgramCardsCategory>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_CARDS.CATEGORY.CREATE,
      payload
    );
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateProgramCardsCategory(id: number, payload: Partial<IProgramCardsCategory>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_CARDS.CATEGORY.UPDATE(id),
      payload
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteProgramCardsCategory(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.PROGRAM_CARDS.CATEGORY.DELETE(id)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getProgramCardsCategoriesByComponentId(componentId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_CARDS.CATEGORY.GET_BY_COMPONENT_ID(componentId)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

// Card (variant 2)
export async function createProgramCardsCard(payload: Partial<IProgramCardsCard>) {
  try {
    const response = await adminAxios.post(
      ADMIN_API_ENDPOINTS.PROGRAM_CARDS.CARD.CREATE,
      payload
    );
    return response?.status === 201 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function updateProgramCardsCard(id: number, payload: Partial<IProgramCardsCard>) {
  try {
    const response = await adminAxios.put(
      ADMIN_API_ENDPOINTS.PROGRAM_CARDS.CARD.UPDATE(id),
      payload
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function deleteProgramCardsCard(id: number) {
  try {
    const response = await adminAxios.delete(
      ADMIN_API_ENDPOINTS.PROGRAM_CARDS.CARD.DELETE(id)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

export async function getProgramCardsCardsByCategoryId(categoryId: number) {
  try {
    const response = await adminAxios.get(
      ADMIN_API_ENDPOINTS.PROGRAM_CARDS.CARD.GET_BY_CATEGORY_ID(categoryId)
    );
    return response?.status === 200 ? response?.data : null;
  } catch (error) {
    const errorResponse = handleApiError(error as AxiosError);
    return errorResponse ? errorResponse : null;
  }
}

